import { FiTrendingUp } from 'react-icons/fi';

export const comparativesSuperlativesData = {
  courseId: 'comparatives-superlatives',
  courseType: 'grammar',
  title: 'Comparatives & Superlatives',
  icon: FiTrendingUp,
  overview: {
    impact: 'Essential for Task 1 data comparison and Speaking',
    focus: 'Forming and using comparative and superlative adjectives',
    uses: 'Writing Task 1, Speaking Part 2',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Forming Comparatives',
      content: `<p>Compare two things:</p>`,
      table: {
        headers: [
          'Type',
          'Rule',
          'Example'
        ],
        rows: [
          [
            'Short (1 syllable)',
            '+ er',
            'tall → taller'
          ],
          [
            'Ending in -e',
            '+ r',
            'large → larger'
          ],
          [
            'CVC',
            'double + er',
            'big → bigger'
          ],
          [
            '2+ syllables',
            'more + adj',
            'expensive → more expensive'
          ],
          [
            'Ending in -y',
            'y → ier',
            'happy → happier'
          ]
        ]
      }
    },
    {
      title: 'Forming Superlatives',
      content: `<p>The highest degree — comparing three or more:</p>`,
      table: {
        headers: [
          'Type',
          'Rule',
          'Example'
        ],
        rows: [
          [
            'Short',
            'the + adj-est',
            'the tallest'
          ],
          [
            '2+ syllables',
            'the most + adj',
            'the most expensive'
          ],
          [
            'Ending in -y',
            'the + adj-iest',
            'the happiest'
          ]
        ]
      }
    },
    {
      title: 'Irregular Forms',
      content: `<p>These must be memorized:</p>`,
      table: {
        headers: [
          'Adjective',
          'Comparative',
          'Superlative'
        ],
        rows: [
          [
            'good',
            'better',
            'the best'
          ],
          [
            'bad',
            'worse',
            'the worst'
          ],
          [
            'far',
            'further/farther',
            'the furthest/farthest'
          ],
          [
            'much/many',
            'more',
            'the most'
          ],
          [
            'little',
            'less',
            'the least'
          ]
        ]
      }
    },
    {
      title: 'Using Comparatives in IELTS Task 1',
      content: `<p>Key structures for data comparison:</p>`,
      points: [
        '\'X is higher/lower than Y\'',
        '\'X is significantly/slightly more expensive than Y\'',
        '\'X has the highest/lowest value\'',
        '\'X is twice/three times as large as Y\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Form the Comparative',
      type: 'select',
      instructions: 'Choose the correct comparative.',
      questions: [
        {
          text: 'This test was ___ than the last one. (difficult)',
          options: [
            'more difficult',
            'difficulter',
            'most difficult'
          ],
          answer: 'more difficult',
          explanation: 'Multi-syllable → \'more difficult\'.'
        },
        {
          text: 'She is ___ than her sister. (tall)',
          options: [
            'taller',
            'more tall',
            'tallest'
          ],
          answer: 'taller',
          explanation: 'One syllable → add -er.'
        },
        {
          text: 'The results were ___ than expected. (good)',
          options: [
            'better',
            'gooder',
            'more good'
          ],
          answer: 'better',
          explanation: 'Irregular: good → better.'
        },
        {
          text: 'This route is ___ than that one. (far)',
          options: [
            'further',
            'farer',
            'more far'
          ],
          answer: 'further',
          explanation: 'Irregular: far → further/farther.'
        }
      ]
    },
    {
      title: 'Form the Comparative (Fill-in)',
      type: 'fill-in',
      instructions: 'Write the correct comparative form of the adjective in brackets.',
      questions: [
        {
          text: 'My house is ___ than yours. (big)',
          answer: 'bigger',
          explanation: 'Short CVC adjective: double the final consonant + er → bigger.'
        },
        {
          text: 'This painting is ___ than that one. (beautiful)',
          answer: 'more beautiful',
          explanation: 'Multi-syllable adjective: use \'more\' + adjective → more beautiful.'
        },
        {
          text: 'She is ___ than her classmate. (happy)',
          answer: 'happier',
          explanation: 'Adjectives ending in -y: change y to i + er → happier.'
        },
        {
          text: 'The new road is ___ than the old one. (wide)',
          answer: 'wider',
          explanation: 'Adjectives ending in -e: just add -r → wider.'
        }
      ]
    },
    {
      title: 'Form the Superlative (Fill-in)',
      type: 'fill-in',
      instructions: 'Write the correct superlative form (include \'the\').',
      questions: [
        {
          text: 'This is ___ building in the city. (tall)',
          answer: 'the tallest',
          explanation: 'Short adjective: the + adjective + est → the tallest.'
        },
        {
          text: 'She is ___ student in our class. (intelligent)',
          answer: 'the most intelligent',
          explanation: 'Multi-syllable adjective: the most + adjective → the most intelligent.'
        },
        {
          text: 'That was ___ day of my life. (happy)',
          answer: 'the happiest',
          explanation: 'Ending in -y: the + adjective with y→iest → the happiest.'
        },
        {
          text: 'It is ___ river in the world. (long)',
          answer: 'the longest',
          explanation: 'Short adjective: the + adjective + est → the longest.'
        }
      ]
    },
    {
      title: 'Irregular Comparisons',
      type: 'select',
      instructions: 'Choose the correct irregular comparative or superlative form.',
      questions: [
        {
          text: 'The weather today is ___ than yesterday. (bad)',
          options: [
            'worse',
            'badder',
            'more bad'
          ],
          answer: 'worse',
          explanation: 'Irregular: bad → worse → the worst.'
        },
        {
          text: 'This is the ___ cake I have ever tasted. (good)',
          options: [
            'best',
            'goodest',
            'most good'
          ],
          answer: 'best',
          explanation: 'Irregular: good → better → the best.'
        },
        {
          text: 'She has ___ experience than her colleague. (much)',
          options: [
            'more',
            'mucher',
            'most'
          ],
          answer: 'more',
          explanation: 'Irregular: much → more → the most.'
        },
        {
          text: 'We need the ___ amount of disruption possible. (little)',
          options: [
            'least',
            'littlest',
            'most little'
          ],
          answer: 'least',
          explanation: 'Irregular: little → less → the least.'
        }
      ]
    }
  ],
  tips: [
    'Short adjectives: -er/-est. Long adjectives: more/most',
    'Never double: \'more better\' is WRONG',
    'Irregular forms must be memorized',
    'Use \'significantly/considerably/slightly\' to qualify comparisons in Task 1'
  ],
  dailyPractice: [
    'Practice comparatives & superlatives concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Comparatives & Superlatives: Short adj + er/est, long adj + more/most, irregular forms. Essential for IELTS Task 1 data descriptions and comparisons.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...comparativesSuperlativesData,
      title: comparativesSuperlativesData.title + ' (Foundations)',
      overview: { ...comparativesSuperlativesData.overview, duration: '15-20 minutes' },
      sections: comparativesSuperlativesData.sections.slice(0, Math.max(2, Math.ceil(comparativesSuperlativesData.sections.length / 2))),
      exercises: comparativesSuperlativesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...comparativesSuperlativesData,
      title: comparativesSuperlativesData.title + ' (Advanced)',
      overview: { ...comparativesSuperlativesData.overview, duration: '30-35 minutes' },
      sections: comparativesSuperlativesData.sections,
      exercises: comparativesSuperlativesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(comparativesSuperlativesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return comparativesSuperlativesData;
}
